import React, { useState, useEffect, useCallback } from 'react';

import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import { submissionsService } from '../../services/submissionsService';

import { paymentsService } from '../../services/paymentsService';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatTimeAgo = (ts) => {
  const diff = Date.now() - new Date(ts)?.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const EVENT_TYPES = {
  submission: {
    label: 'Form Submission',
    icon: 'FileCheck2',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
  },
  payment: {
    label: 'Payment',
    icon: 'CreditCard',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
  },
  milestone: {
    label: 'Milestone',
    icon: 'Trophy',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
  },
};

const STATUS_META = {
  success: { label: 'Success', dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  failed: { label: 'Failed', dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50' },
  pending: { label: 'Pending', dot: 'bg-amber-400', text: 'text-amber-700', bg: 'bg-amber-50' },
  info: { label: 'Info', dot: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50' },
};

// ─── Mock event generator ────────────────────────────────────────────────────

const buildMockEvents = () => {
  const now = Date.now();
  return [
    { id: 'e1', type: 'submission', status: 'success', title: 'New submission on "Contact Us"', detail: 'john.doe@example.com submitted the Contact Us form', form: 'Contact Us', ts: now - 2 * 60000 },
    { id: 'e2', type: 'payment', status: 'success', title: 'Payment confirmed — $149.00', detail: 'Stripe payment intent pi_3Qx… confirmed for Pro Plan upgrade', form: 'Pro Plan Checkout', ts: now - 8 * 60000 },
    { id: 'e4', type: 'milestone', status: 'info', title: '🎉 100 submissions reached on "Survey 2026"', detail: 'Your form hit the 100-submission milestone — great engagement!', form: 'Survey 2026', ts: now - 32 * 60000 },
    { id: 'e5', type: 'submission', status: 'success', title: 'New submission on "Job Application"', detail: 'sarah.k@acme.com submitted the Job Application form', form: 'Job Application', ts: now - 47 * 60000 },
    { id: 'e6', type: 'payment', status: 'failed', title: 'Payment declined — $29.00', detail: 'Card declined (insufficient funds) for Basic Plan checkout', form: 'Basic Plan Checkout', ts: now - 1.2 * 3600000 },
    { id: 'e8', type: 'submission', status: 'success', title: 'New submission on "Event RSVP"', detail: 'mike.t@corp.io submitted the Event RSVP form', form: 'Event RSVP', ts: now - 3 * 3600000 },
    { id: 'e9', type: 'milestone', status: 'info', title: '🚀 Form "Product Feedback" went live', detail: 'Your form was published and is now accepting responses', form: 'Product Feedback', ts: now - 5 * 3600000 },
    { id: 'e10', type: 'payment', status: 'success', title: 'Payment confirmed — $299.00', detail: 'Stripe payment confirmed for Annual Business Plan', form: 'Business Plan Checkout', ts: now - 6 * 3600000 },
    { id: 'e11', type: 'submission', status: 'success', title: 'New submission on "Newsletter Signup"', detail: 'alice@startup.io subscribed via Newsletter Signup form', form: 'Newsletter Signup', ts: now - 8 * 3600000 },
    { id: 'e13', type: 'milestone', status: 'info', title: '📈 500 total submissions this month', detail: 'You\'ve crossed 500 submissions for July 2026 — a new record!', form: 'All Forms', ts: now - 12 * 3600000 },
    { id: 'e14', type: 'payment', status: 'pending', title: 'Payment processing — $79.00', detail: 'Payment intent created, awaiting 3D Secure confirmation', form: 'Starter Plan Checkout', ts: now - 14 * 3600000 },
    { id: 'e15', type: 'submission', status: 'success', title: 'New submission on "Bug Report"', detail: 'dev@company.com submitted a bug report with 3 attachments', form: 'Bug Report', ts: now - 18 * 3600000 },
    { id: 'e16', type: 'milestone', status: 'info', title: '⭐ A/B test "CTA Variant B" declared winner', detail: 'Variant B achieved 34% higher conversion — test concluded', form: 'Lead Capture', ts: now - 22 * 3600000 },
    { id: 'e17', type: 'submission', status: 'success', title: 'New submission on "Feedback Form"', detail: 'Anonymous user submitted a 5-star rating with comments', form: 'Feedback Form', ts: now - 26 * 3600000 },
    { id: 'e18', type: 'payment', status: 'success', title: 'Refund processed — $49.00', detail: 'Refund issued to customer card ending in 4242', form: 'Pro Plan Checkout', ts: now - 30 * 3600000 },
  ].map((event) => ({ ...event, isPreview: true }));
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, sub, color }) => (
  <div className="bg-card border border-border rounded-lg p-4 flex items-start gap-3">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon name={icon} size={20} />
    </div>
    <div>
      <p className="text-2xl font-heading font-bold text-foreground leading-tight">{value}</p>
      <p className="text-sm font-medium text-foreground">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const meta = STATUS_META?.[status] || STATUS_META?.info;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${meta.bg} ${meta.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

const EventRow = ({ event }) => {
  const meta = EVENT_TYPES?.[event?.type] || EVENT_TYPES?.submission;
  return (
    <div className="flex items-start gap-4 px-5 py-4 hover:bg-muted/40 transition-colors group border-b border-border last:border-0">
      {/* Icon */}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${meta.bg} ${meta.border}`}>
        <Icon name={meta.icon} size={18} className={meta.color} />
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <p className="text-sm font-medium text-foreground leading-snug">{event?.title}</p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <StatusBadge status={event?.status} />
            <span className="text-xs text-muted-foreground whitespace-nowrap">{formatTimeAgo(event?.ts)}</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{event?.detail}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${meta.badge}`}>{meta.label}</span>
          {event?.isPreview && (
            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Preview</span>
          )}
          {event?.form && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Icon name="FileText" size={11} />
              {event?.form}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

export const ActivityHubContent = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      // Attempt to pull real data and merge with mock events
      let realEvents = [];

      // Submissions
      try {
        const subs = await submissionsService?.getAllSubmissions?.();
        if (Array.isArray(subs)) {
          realEvents = realEvents?.concat(
            subs?.slice(0, 5)?.map((s, i) => ({
              id: `sub-${s?.id || i}`,
              type: 'submission',
              status: 'success',
              title: `New submission on "${s?.form_name || s?.formName || 'Form'}"`,
              detail: `Submission received${s?.email ? ` from ${s?.email}` : ''}`,
              form: s?.form_name || s?.formName || 'Form',
              ts: new Date(s.created_at || s.createdAt || Date.now())?.getTime(),
            }))
          );
        }
      } catch (_) {}

      // Payments
      try {
        const payments = await paymentsService?.getTransactions?.();
        if (Array.isArray(payments)) {
          realEvents = realEvents?.concat(
            payments?.slice(0, 5)?.map((p, i) => ({
              id: `pay-${p?.id || i}`,
              type: 'payment',
              status: p?.status === 'succeeded' ? 'success' : p?.status === 'pending' ? 'pending' : 'failed',
              title: `Payment ${p?.status === 'succeeded' ? 'confirmed' : p?.status} — $${((p?.amount || 0) / 100)?.toFixed(2)}`,
              detail: `Transaction ${p?.id || ''} via ${p?.payment_method || 'Stripe'}`,
              form: p?.form_name || 'Checkout',
              ts: new Date(p.created_at || Date.now())?.getTime(),
            }))
          );
        }
      } catch (_) {}

      // Merge real + mock, deduplicate by id, sort by ts desc
      const mock = buildMockEvents();
      const merged = realEvents?.length > 0
        ? [...realEvents, ...mock]?.sort((a, b) => b?.ts - a?.ts)
        : mock;

      setEvents(merged);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('ActivityHub load error:', err);
      setEvents(buildMockEvents());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Auto-refresh every 30s
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadEvents, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadEvents]);

  // Derived stats
  const stats = {
    total: events?.length,
    submissions: events?.filter(e => e?.type === 'submission')?.length,
    failures: events?.filter(e => e?.status === 'failed')?.length,
    payments: events?.filter(e => e?.type === 'payment' && e?.status === 'success')?.length,
    milestones: events?.filter(e => e?.type === 'milestone')?.length,
  };

  // Filtered events
  const filtered = events?.filter(e => {
    const matchType = activeFilter === 'all' || e?.type === activeFilter;
    const matchStatus = statusFilter === 'all' || e?.status === statusFilter;
    const matchSearch = !searchQuery ||
      e?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      e?.detail?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      e?.form?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchType && matchStatus && matchSearch;
  });

  const filterTabs = [
    { key: 'all', label: 'All Events', icon: 'Activity' },
    { key: 'submission', label: 'Submissions', icon: 'FileCheck2' },
    { key: 'payment', label: 'Payments', icon: 'CreditCard' },
    { key: 'milestone', label: 'Milestones', icon: 'Trophy' },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Activity Hub</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Centralized feed of all platform events — submissions, payments, and milestones
              </p>
              <p className="mt-2 text-xs text-amber-700">
                Unmarked response events are loaded from your account. Every row marked Preview is example data until event tracking is connected.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAutoRefresh(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  autoRefresh
                    ? 'bg-primary/10 border-primary/20 text-primary' :'bg-muted border-border text-muted-foreground'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? 'bg-primary animate-pulse' : 'bg-muted-foreground'}`} />
                {autoRefresh ? 'Live' : 'Paused'}
              </button>
              <button
                onClick={loadEvents}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-card border border-border text-foreground hover:bg-muted transition-colors"
              >
                <Icon name="RefreshCw" size={13} />
                Refresh
              </button>
              <span className="text-xs text-muted-foreground hidden sm:block">
                Updated {formatTimeAgo(lastRefreshed)}
              </span>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatCard icon="Activity" label="Total Events" value={stats?.total} sub="Last 24 hours" color="bg-primary/10 text-primary" />
            <StatCard icon="FileCheck2" label="Submissions" value={stats?.submissions} sub="Forms received" color="bg-emerald-100 text-emerald-600" />
            <StatCard icon="AlertTriangle" label="Failures" value={stats?.failures} sub="Need attention" color="bg-red-100 text-red-600" />
            <StatCard icon="CreditCard" label="Payments" value={stats?.payments} sub="Confirmed" color="bg-blue-100 text-blue-600" />
          </div>

          {/* Filters + Search */}
          <div className="bg-card border border-border rounded-lg mb-4">
            {/* Type tabs */}
            <div className="flex items-center gap-1 p-3 border-b border-border overflow-x-auto scrollbar-custom">
              {filterTabs?.map(tab => (
                <button
                  key={tab?.key}
                  onClick={() => setActiveFilter(tab?.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                    activeFilter === tab?.key
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={13} />
                  {tab?.label}
                  {tab?.key !== 'all' && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                      activeFilter === tab?.key ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'
                    }`}>
                      {events?.filter(e => e?.type === tab?.key)?.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Search + status filter */}
            <div className="flex items-center gap-3 p-3 flex-wrap">
              <div className="relative flex-1 min-w-[180px]">
                <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search events…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-muted border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <div className="flex items-center gap-1">
                {['all', 'success', 'failed', 'pending']?.map(s => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                      statusFilter === s
                        ? 'bg-foreground text-background'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {s === 'all' ? 'All Status' : s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Event Feed */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Icon name="Activity" size={16} className="text-primary" />
                <span className="text-sm font-semibold text-foreground">
                  {filtered?.length} event{filtered?.length !== 1 ? 's' : ''}
                </span>
                {(activeFilter !== 'all' || statusFilter !== 'all' || searchQuery) && (
                  <span className="text-xs text-muted-foreground">(filtered)</span>
                )}
              </div>
              {(activeFilter !== 'all' || statusFilter !== 'all' || searchQuery) && (
                <button
                  onClick={() => { setActiveFilter('all'); setStatusFilter('all'); setSearchQuery(''); }}
                  className="text-xs text-primary hover:text-primary/80 transition-colors font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">Loading events…</p>
              </div>
            ) : filtered?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                  <Icon name="Inbox" size={24} className="text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-foreground">No events found</p>
                <p className="text-xs text-muted-foreground">Try adjusting your filters or search query</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filtered?.map(event => (
                  <EventRow key={event?.id} event={event} />
                ))}
              </div>
            )}
          </div>

          {/* Footer note */}
          <p className="text-xs text-muted-foreground text-center mt-4">
            Showing events from the last 24 hours · Auto-refreshes every 30 seconds
          </p>
    </div>
  );
};

const ActivityHub = () => (
  <NavigationBar>
    <ActivityHubContent />
  </NavigationBar>
);

export default ActivityHub;
